import { IdentityObserver, IdentityEventTypes, IdentityEvent } from "@shopkeep/lsid-identity-observer";
import { LSIDUserIdentity } from "@shopkeep/lsid-types";
import { updateLsidIdentity } from "actions/lsid-actions";
import * as Sentry from "@sentry/browser";

import me from "models/me";
import { getStore } from "store";
import { getConfig } from "site-config";
import { SearchSettings } from "constants/types";

export class LSIDIdentityObserver {
    private observer: IdentityObserver;
    private unsubscribe: () => void;

    constructor() {
        const { LSID_IDENTITY_ROOT } = getConfig();
        this.observer = new IdentityObserver({
            lsApiBaseUrl: LSID_IDENTITY_ROOT,
        });
    }

    public subscribe = (): void => {
        this.unsubscribe = this.observer.subscribe(
            (event) => {
                this.subscriber(event);
            },
            { fetchOnInit: true }
        );
    };

    public canSubscribe = (pathname: string, mobileQueryParam: boolean): boolean => {
        /**
         * Currently the identity observer work does not extend to the mobile application.
         * If the mobile query param is present or the userAgent is a mobile device we do not subscribe.
         * Use the Launch Darkly lsid_observer flag to control subscription.
         * If the LSID_FEATURE_LSID is not enabled do not subscribe.
         * This is to ensure we can turn it off for local development.
         * If the user is on the login page do not subscribe.
         * This is to ensure that the user has to re-authenticate as NuOrder does not support autoLogin.
         */
        const isNotLoginPage = pathname !== "/" && pathname !== "/login";
        const isMobile = /iPad|iPhone|iPod/.test(navigator.userAgent) || mobileQueryParam === true;
        const { lsid_observer } = me.ldClient.allFlags();
        const { LSID_FEATURE_LSID } = getConfig();

        if (!isMobile && isNotLoginPage && lsid_observer && LSID_FEATURE_LSID === "true") {
            return true;
        }

        return false;
    };

    public identityObserverUnsubscribe = (): void => {
        if (this.unsubscribe) {
            this.unsubscribe();
        }
    };

    public subscriber = (event: IdentityEvent): void => {
        const { LSID_PRODUCT } = getConfig();
        const store = getStore();
        switch (event.type) {
            case IdentityEventTypes.UNAUTHORIZED:
                /**
                 * Can be triggered by a user logging out of a linked product.
                 * Is also triggered by the 72 hour session expiring
                 */
                this.logOut();
                break;
            case IdentityEventTypes.UPDATED: {
                const isSession = this.observer.isSession();
                const hasUserGrantForProduct = !!event.identity?.grants?.user?.[LSID_PRODUCT];
                const currentUserIsAuthenticated = event.identity?.grants?.user?.[LSID_PRODUCT]?.includes(me._id);
                /**
                 * This is true when a user in retail performs a pin switch.
                 * This is equivalent to the user logging out of retail.
                 */
                if (isSession) {
                    this.logOut();
                }
                /**
                 * This can occur when a user logs out of a linked-product (ie: retail) in a separate tab.
                 * Then a new user logs in to that product with a LSID user that has a different user linked for your product (ie: nuorder).
                 */
                if (!hasUserGrantForProduct) {
                    this.logOut();
                }
                /**
                 * This can occur when a user logs out of a linked-product (ie: retail) in a separate tab.
                 * Then a new user logs in to that product with a LSID user that has a different user linked for your product (ie: nuorder).
                 */
                if (hasUserGrantForProduct && !currentUserIsAuthenticated) {
                    this.logOut();
                }

                if (currentUserIsAuthenticated) {
                    store.dispatch(updateLsidIdentity(this.formatPayload(event.identity as any)));
                }

                break;
            }

            case IdentityEventTypes.ERROR:
                Sentry.captureException(`LSID Identity Observer Error: ${event.error}`, {
                    level: "error",
                    user: { id: me._id, email: me.email },
                });
                return;
            default:
                return;
        }
    };

    private logOut = (): void => {
        if (this.observer.isAutoLogoutEnabled()) {
            localStorage.removeItem(SearchSettings.SearchType);
            me.logout();
        }
    };

    private formatPayload = (identity: LSIDUserIdentity): any => {
        return {
            userGrants: identity?.grants?.user ?? {},
            accountGrants: identity?.grants?.account ?? {},
            features: identity?.features ?? [],
            accountFeatures: identity?.accountFeatures ?? {},
        };
    };
}
